import React, { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Fingerprint,
  Plus,
  Trash2,
  Pencil,
  Check,
  X,
  Loader2,
  Monitor,
  Smartphone,
  AlertCircle,
  Info,
  Shield,
  Cloud
} from 'lucide-react'
import toast from 'react-hot-toast'
import passkeyService from '../../services/passkeyService'
import ConfirmDialog from '../ConfirmDialog'
import useConfirmDialog from '../../hooks/useConfirmDialog'
import { createLogger } from '../../utils/logger'

const logger = createLogger('PasskeyManagement')

const PasskeyManagement = () => {
  const [passkeys, setPasskeys] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isRegistering, setIsRegistering] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [editName, setEditName] = useState('')
  const [newPasskeyName, setNewPasskeyName] = useState('')
  const [showNameInput, setShowNameInput] = useState(false)
  const [isSupported, setIsSupported] = useState(true)
  const [canAddMore, setCanAddMore] = useState(true)
  const [maxAllowed, setMaxAllowed] = useState(5)
  const { isOpen, config, closeDialog, handleConfirm, confirmDialog } = useConfirmDialog()

  // Check WebAuthn support on mount
  useEffect(() => {
    setIsSupported(passkeyService.isSupported())
  }, [])

  // Load passkeys on mount
  useEffect(() => {
    loadPasskeys()
  }, [])

  const loadPasskeys = useCallback(async () => {
    setIsLoading(true)
    try {
      const data = await passkeyService.listPasskeys()
      setPasskeys(data.passkeys || [])
      setCanAddMore(data.canAddMore)
      setMaxAllowed(data.maxAllowed)
    } catch (error) {
      logger.error('Failed to load passkeys:', error)
      toast.error('Failed to load passkeys')
    } finally {
      setIsLoading(false)
    }
  }, [])

  const handleAddPasskey = async () => {
    if (!isSupported) {
      toast.error('Passkeys are not supported on this browser')
      return
    }

    if (!canAddMore) {
      toast.error(`Maximum of ${maxAllowed} passkeys allowed`)
      return
    }

    setIsRegistering(true)
    try {
      const name = newPasskeyName.trim() || undefined
      await passkeyService.registerPasskey(name)
      toast.success('Passkey registered successfully!')
      setShowNameInput(false)
      setNewPasskeyName('')
      await loadPasskeys()
    } catch (error) {
      logger.error('Failed to register passkey:', error)
      // Handle specific WebAuthn errors
      if (error.name === 'NotAllowedError') {
        toast.error('Passkey registration was cancelled')
      } else if (error.name === 'InvalidStateError') {
        toast.error('This passkey is already registered')
      } else if (error.response?.data?.code === 'PASSKEY_LIMIT_REACHED') {
        toast.error(`Maximum of ${maxAllowed} passkeys allowed`)
        setCanAddMore(false)
      } else {
        toast.error(error.response?.data?.error || 'Failed to register passkey')
      }
    } finally {
      setIsRegistering(false)
    }
  }

  const handleDeletePasskey = async (id, name) => {
    const confirmed = await confirmDialog({
      title: 'Delete Passkey',
      message: `Are you sure you want to delete "${name || 'this passkey'}"?`,
      confirmText: 'Delete',
      cancelText: 'Cancel'
    })

    if (!confirmed) {
      return
    }

    setDeletingId(id)
    try {
      await passkeyService.deletePasskey(id)
      toast.success('Passkey deleted successfully')
      await loadPasskeys()
    } catch (error) {
      logger.error('Failed to delete passkey:', error)
      toast.error(error.response?.data?.error || 'Failed to delete passkey')
    } finally {
      setDeletingId(null)
    }
  }

  const handleStartEdit = (passkey) => {
    setEditingId(passkey.id)
    setEditName(passkey.name || '')
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditName('')
  }

  const handleSaveEdit = async (id) => {
    const newName = editName.trim()
    if (!newName) {
      toast.error('Name is required')
      return
    }

    try {
      await passkeyService.renamePasskey(id, newName)
      toast.success('Passkey renamed successfully')
      setEditingId(null)
      setEditName('')
      await loadPasskeys()
    } catch (error) {
      logger.error('Failed to rename passkey:', error)
      toast.error(error.response?.data?.error || 'Failed to rename passkey')
    }
  }

  const getDeviceIcon = (deviceType) => {
    if (deviceType === 'singleDevice') {
      return <Smartphone className="w-5 h-5 text-gray-600 dark:text-gray-400" />
    }
    return <Monitor className="w-5 h-5 text-gray-600 dark:text-gray-400" />
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Fingerprint className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Passkeys</h4>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            ({passkeys.length}/{maxAllowed})
          </span>
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Passkeys let you sign in securely using Face ID, Touch ID, or Windows Hello instead of a
        password.
      </p>

      {/* Browser Support Warning */}
      {!isSupported && (
        <div className="flex items-start gap-2 p-3 mb-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
          <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-700 dark:text-amber-300">
            Passkeys are not supported in this browser. Please use a modern browser like Chrome,
            Safari, or Edge.
          </p>
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      ) : (
        <>
          {/* Passkeys List */}
          <div className="space-y-3 mb-4">
            <AnimatePresence>
              {passkeys.length === 0 ? (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                  <Shield className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No passkeys registered yet</p>
                </div>
              ) : (
                passkeys.map((passkey) => (
                  <motion.div
                    key={passkey.id}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
                  >
                    {/* Device Icon */}
                    <div className="flex-shrink-0">{getDeviceIcon(passkey.deviceType)}</div>

                    {/* Passkey Info */}
                    <div className="flex-1 min-w-0">
                      {editingId === passkey.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            maxLength={50}
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveEdit(passkey.id)
                              if (e.key === 'Escape') handleCancelEdit()
                            }}
                          />
                          <button
                            onClick={() => handleSaveEdit(passkey.id)}
                            className="p-1 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                            title="Save"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="p-1 text-gray-600 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {passkey.name || 'Unnamed Passkey'}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                            <span>Added {formatDate(passkey.createdAt)}</span>
                            {passkey.backedUp && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 rounded">
                                <Cloud className="w-3 h-3" />
                                Synced
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Actions */}
                    {editingId !== passkey.id && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleStartEdit(passkey)}
                          className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                          title="Rename"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeletePasskey(passkey.id, passkey.name)}
                          disabled={deletingId === passkey.id}
                          className="p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                          title="Delete"
                        >
                          {deletingId === passkey.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    )}
                  </motion.div>
                ))
              )}
            </AnimatePresence>
          </div>

          {/* Add Passkey Section */}
          {showNameInput ? (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Passkey Name (optional)
                </label>
                <input
                  type="text"
                  value={newPasskeyName}
                  onChange={(e) => setNewPasskeyName(e.target.value)}
                  placeholder="e.g., MacBook Pro, iPhone"
                  maxLength={50}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  autoFocus
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleAddPasskey}
                  disabled={isRegistering}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50"
                >
                  {isRegistering ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Registering...</span>
                    </>
                  ) : (
                    <>
                      <Fingerprint className="w-4 h-4" />
                      <span>Register Passkey</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowNameInput(false)
                    setNewPasskeyName('')
                  }}
                  disabled={isRegistering}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => {
                setNewPasskeyName(`Passkey ${passkeys.length + 1}`)
                setShowNameInput(true)
              }}
              disabled={!isSupported || !canAddMore || isRegistering}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              <span>Add Passkey</span>
            </button>
          )}

          {/* Limit reached message */}
          {!canAddMore && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
              Maximum number of passkeys reached. Delete one to add a new passkey.
            </p>
          )}

          {/* Info Notice */}
          <div className="flex items-start gap-2 p-3 mt-4 bg-gray-100 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <Info className="w-4 h-4 text-gray-500 dark:text-gray-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Passkeys are stored securely on your device and can sync across your devices through
              iCloud Keychain, Google Password Manager, or similar services.
            </p>
          </div>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog isOpen={isOpen} onClose={closeDialog} onConfirm={handleConfirm} {...config} />
    </div>
  )
}

export default PasskeyManagement
